import {createContext, PropsWithChildren, useCallback, useContext, useMemo, useState} from "react";
import {colors} from "../theme";
import type {DrawerConfig} from "./Drawer";

export type NewDesignLayoutConfig = {
  backgroundColor?: string;
  background?: string;
  gradientBg?: boolean;
  hideNavbar?: boolean;
  hideFooter?: boolean;
  fullPage?: boolean;
  floatingCta?: boolean;
};

export const NEW_DESIGN_DEFAULTS: NewDesignLayoutConfig = {
  backgroundColor: colors.tertiary[100],
};

type NewDesignContextValue = {
  layoutConfig: NewDesignLayoutConfig;
  drawerConfig: DrawerConfig | undefined;
  setLayoutConfig: (config: NewDesignLayoutConfig) => void;
  setDrawerConfig: (config: DrawerConfig | undefined) => void;
};

const NewDesignContext = createContext<NewDesignContextValue | null>(null);

export const useNewDesignContext = () => {
  const ctx = useContext(NewDesignContext);
  if (!ctx) throw new Error("useNewDesignContext must be used within NewDesignLayout");
  return ctx;
};

export const NewDesignProvider = ({children}: PropsWithChildren) => {
  const [layoutConfig, setLayoutConfig] = useState<NewDesignLayoutConfig>(NEW_DESIGN_DEFAULTS);
  const [drawerConfig, setDrawerConfig] = useState<DrawerConfig | undefined>();

  const stableSetConfig = useCallback((next: NewDesignLayoutConfig) => {
    setLayoutConfig((prev) => {
      if (
        prev.backgroundColor === next.backgroundColor &&
        prev.background === next.background &&
        prev.gradientBg === next.gradientBg &&
        prev.hideNavbar === next.hideNavbar &&
        prev.hideFooter === next.hideFooter &&
        prev.fullPage === next.fullPage &&
        prev.floatingCta === next.floatingCta
      )
        return prev;
      return next;
    });
  }, []);

  const ctx = useMemo<NewDesignContextValue>(
    () => ({layoutConfig, drawerConfig, setLayoutConfig: stableSetConfig, setDrawerConfig}),
    [layoutConfig, drawerConfig, stableSetConfig]
  );

  return <NewDesignContext.Provider value={ctx}>{children}</NewDesignContext.Provider>;
};
